import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Box, Container, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { TextV2 } from '@insights-ltd/design-library/components';
import { ChangeOrganisation } from 'components/ChangeOrganisation';
import { useAuth } from 'contexts/AuthContext';
import FullHeight from 'components/FullHeight';
import Breadcrumbs from 'components/Breadcrumbs';
import { OrganisationResponse } from 'api';
import useGetMyOrganisations from 'components/hooks/useGetMyOrganisations';
import FullScreenSpinner from 'components/FullScreen/FullScreenSpinner';
import FullScreenError from 'components/FullScreen/FullScreenError';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { queryStatus } from 'utils/queryStatus';
import { useSearchContext } from 'components/SearchFilter/SearchProvider';
import { useEffect, useState } from 'react';
import { getMinimumSearchCharacters, SearchMode } from 'api/search';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import { useLocation, useNavigate } from 'react-router';
import EvaluatorLinkList from './EvaluatorLinkList/EvaluatorLinkList';
import CreateEvaluatorLink from './CreateEvaluator/CreateEvaluatorLink';
import useSortedEvaluatorLinks from './useSortedEvaluatorLinks';

const StyledH3 = styled(TextV2)(({ theme }) => ({
  margin: `${theme.spacing(spacingSizeMap.S)} 0 ${theme.spacing(
    spacingSizeMap.M,
  )} !important`,
}));

const EvaluatorLinksFilterCategories = ['ALL', 'PRACTITIONER'] as const;
type EvaluatorLinksFilterCategory =
  (typeof EvaluatorLinksFilterCategories)[number];

const EvaluatorLinks = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const isAbleToSee3DRS = user?.licensedProducts.includes(
    'DISCOVERY_SELF_AWARE_PROFILE',
  );

  const {
    organisations,
    totalCount,
    status: orgStatus,
  } = useGetMyOrganisations();

  const defaultOrganisation = totalCount === 1 ? organisations?.[0] : null;

  const {
    searchState: {
      organisation = defaultOrganisation,
      filter = 'ALL',
      searchTerm = '',
    },
    setSearchState,
  } = useSearchContext<EvaluatorLinksFilterCategory>(
    EvaluatorLinksFilterCategories,
  );
  const { state: locationState, pathname } = useLocation();
  const navigatedToOrganisation = locationState?.organisation;
  const navigate = useNavigate();
  useEffect(() => {
    if (navigatedToOrganisation) {
      setSearchState({ organisation: navigatedToOrganisation });
      navigate(pathname, { replace: true, state: {} });
    }
  }, [navigate, navigatedToOrganisation, pathname, setSearchState]);
  const [searchMode, setSearchMode] = useState<SearchMode>('STARTS_WITH');
  const debouncedSearchTerm = useDebounceValue(searchTerm);

  const querySearchTerm = debouncedSearchTerm || undefined;

  const minChars = getMinimumSearchCharacters(searchMode);
  const isSearchTooShort =
    searchTerm.length > 0 && searchTerm.length < minChars;

  const setOrganisation = (org: OrganisationResponse | null) => {
    setSearchState({ organisation: org });
  };

  const setFilterCategory = (filterCategory: EvaluatorLinksFilterCategory) => {
    setSearchState({ filter: filterCategory });
  };

  const setSearchTerm = (term: string) => {
    setSearchState({ searchTerm: term });
  };

  const organisationId = organisation?.id ?? null;
  const createdBy = filter === 'PRACTITIONER' ? user?.id : null;
  const {
    paginator,
    evaluatorStatus,
    setLinkSortBy,
    sortCategory,
    apiPaginationProps,
    showResultsLoadingState,
  } = useSortedEvaluatorLinks(
    organisationId,
    createdBy,
    querySearchTerm,
    searchMode,
  );
  const { handlePageChange } = paginator;

  const status = queryStatus(orgStatus, evaluatorStatus);

  const handleSetFilterCategory = (value: EvaluatorLinksFilterCategory) => {
    setFilterCategory(value);
    handlePageChange(1);
  };

  const handleSetSearchTerm = (value: string) => {
    setSearchTerm(value);
    handlePageChange(1);
  };

  const handleSetSearchMode = (mode: SearchMode) => {
    setSearchMode(mode);
    handlePageChange(1);
  };

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }

  if (status === 'error') {
    return <FullScreenError message={t('ui.event-management.generic.error')} />;
  }

  const listElement = (
    <EvaluatorLinkList
      isOrgSpecific={!!organisationId}
      filter={filter as EvaluatorLinksFilterCategory}
      paginator={paginator}
      apiPaginationProps={apiPaginationProps}
      searchTerm={searchTerm}
      searchMode={searchMode}
      onFilterChange={(value) => {
        handleSetFilterCategory(value);
      }}
      onSearchTermChange={(value) => {
        handleSetSearchTerm(value);
      }}
      onSearchModeChange={handleSetSearchMode}
      sortCategory={sortCategory}
      setLinkSortBy={setLinkSortBy}
      isSearchTooShort={isSearchTooShort}
      showResultsLoadingState={showResultsLoadingState}
    />
  );

  return (
    <>
      <Helmet>
        <title>{t('ui.event-management.title.evaluator-links')}</title>
      </Helmet>
      <FullHeight>
        <Container maxWidth="lg">
          <Box sx={{ paddingTop: '25px', paddingBottom: '25px' }}>
            <Breadcrumbs
              crumbs={{
                '/': t('ui.event-management.events.nav.home'),
              }}
              activeText={t('ui.event-management.evaluator-links.title')}
            />
          </Box>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.S),
                }}
              >
                <TextV2 variant="h2">
                  {t('ui.event-management.evaluator-links.title')}
                </TextV2>
              </Box>
              <Box>
                <TextV2 color="textSecondary">
                  {t('ui.event-management.evaluator-links.header')}
                </TextV2>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 'auto' }}>
              {totalCount !== undefined && totalCount > 1 ? (
                <ChangeOrganisation
                  disabled={false}
                  organisation={organisation ?? null}
                  handleOrganisationChange={(value) => {
                    setOrganisation(value);
                    handleSetSearchTerm('');
                    setSearchMode('STARTS_WITH');
                  }}
                  hasAllOrgsOption
                />
              ) : null}
            </Grid>
          </Grid>
          <Box
            sx={{
              marginTop: '36px',
              '& > h3': {
                marginTop: '0 !important',
                marginBottom: '0.75rem !important',
              },
            }}
          >
            <StyledH3 variant="h3">
              {t('ui.event-management.evaluator-links.create-link.title')}
            </StyledH3>
            <CreateEvaluatorLink
              defaultDialect="en-GB"
              organisationId={organisationId}
              disabled={!isAbleToSee3DRS}
            />
          </Box>
          {isAbleToSee3DRS ? listElement : null}
        </Container>
      </FullHeight>
    </>
  );
};

export default EvaluatorLinks;
